import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Minus, Plus, Flame, Upload, X, Loader2, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import api, { money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Stars } from "@/components/Stars";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { EngravingPreview, FontPicker } from "@/components/EngravingPreview";
import { ArtworkSetup } from "@/components/ArtworkSetup";
import { ENGRAVING_FONTS } from "@/data/engravingPreview";
import { breadcrumbSchema, productSchema, productMetaDescription } from "@/lib/seo";

const ReviewsSection = ({ slug }) => {
  const [data, setData] = useState({ reviews: [], average: 0, count: 0 });
  const [form, setForm] = useState({ name: "", email: "", rating: 0, comment: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get(`/reviews/${slug}`).then((r) => setData(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.rating || !form.comment.trim()) {
      toast.error("Add your name, a star rating and a few words.");
      return;
    }
    setSubmitting(true);
    try {
      const { data: created } = await api.post("/reviews", { product_slug: slug, ...form });
      setForm({ name: "", email: "", rating: 0, comment: "" });
      toast.success(created.verified ? "Thanks — posted with a verified purchase badge." : "Thanks — your review is live.");
      load();
    } catch {
      toast.error("Could not post review — try again.");
    }
    setSubmitting(false);
  };

  return (
    <section className="mt-24 border-t border-zinc-900 pt-16" data-testid="reviews-section">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <div className="flex items-end justify-between gap-6 mb-10">
            <div>
              <span className="font-mono-tech text-xs uppercase tracking-[0.35em] text-[#C87455]">Reviews</span>
              <h2 className="font-serif-display font-light text-3xl text-white mt-3 tracking-tight">What customers say</h2>
            </div>
            {data.count > 0 && (
              <div className="flex items-center gap-3 shrink-0" data-testid="reviews-average">
                <Stars value={data.average} />
                <span className="font-mono-tech text-sm text-zinc-400">{data.average} · {data.count} review{data.count > 1 ? "s" : ""}</span>
              </div>
            )}
          </div>
          <div className="space-y-8">
            {data.reviews.length === 0 && (
              <p className="font-mono-tech text-sm text-zinc-600 uppercase tracking-widest" data-testid="reviews-empty">Be the first to review this piece</p>
            )}
            {data.reviews.map((r) => (
              <div key={r.id} className="border-b border-zinc-900 pb-8" data-testid={`review-${r.id}`}>
                <div className="flex items-center justify-between gap-4 mb-3">
                  <div className="flex items-center gap-4 flex-wrap">
                    <span className="font-serif-display text-lg text-white">{r.name}</span>
                    <Stars value={r.rating} size={13} />
                    {r.verified && (
                      <span className="flex items-center gap-1 font-mono-tech text-[9px] uppercase tracking-wider text-emerald-400/80" data-testid={`verified-badge-${r.id}`}>
                        <BadgeCheck size={12} /> Verified purchase
                      </span>
                    )}
                  </div>
                  <span className="font-mono-tech text-[10px] text-zinc-600 uppercase tracking-wider">
                    {new Date(r.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-zinc-400 text-sm leading-relaxed">{r.comment}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="lg:col-span-5">
          <Reveal>
            <form onSubmit={submit} className="bg-[#0A0A0A] border border-zinc-900 p-8 lg:sticky lg:top-32" data-testid="review-form">
              <h3 className="font-serif-display text-2xl text-white mb-6">Leave a review</h3>
              <div className="space-y-6">
                <div>
                  <label className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">Your rating</label>
                  <Stars value={form.rating} onChange={(n) => setForm({ ...form, rating: n })} size={22} />
                </div>
                <input
                  data-testid="review-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name"
                  className="w-full bg-transparent border-b border-zinc-800 focus:border-[#C87455] transition-colors rounded-none px-0 py-3 text-white placeholder-zinc-600 text-sm"
                />
                <div>
                  <input
                    data-testid="review-email-input"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="Email (optional)"
                    className="w-full bg-transparent border-b border-zinc-800 focus:border-[#C87455] transition-colors rounded-none px-0 py-3 text-white placeholder-zinc-600 text-sm"
                  />
                  <p className="font-mono-tech text-[9px] uppercase tracking-wider text-zinc-600 mt-2 flex items-center gap-1">
                    <BadgeCheck size={11} className="text-emerald-400/60" /> Use your checkout email to earn a verified purchase badge — never shown publicly
                  </p>
                </div>
                <textarea
                  data-testid="review-comment-input"
                  value={form.comment}
                  onChange={(e) => setForm({ ...form, comment: e.target.value.slice(0, 600) })}
                  rows={3}
                  placeholder="How did your piece turn out?"
                  className="w-full bg-transparent border-b border-zinc-800 focus:border-[#C87455] transition-colors rounded-none px-0 py-3 text-white placeholder-zinc-600 text-sm resize-none"
                />
                <button
                  data-testid="review-submit-btn"
                  type="submit"
                  disabled={submitting}
                  className="bg-[#C87455] text-black hover:bg-[#A65B3E] transition-colors px-8 py-3 font-mono-tech uppercase tracking-[0.2em] text-sm disabled:opacity-60"
                >
                  {submitting ? "Posting…" : "Post Review"}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [missing, setMissing] = useState(false);
  const [engraving, setEngraving] = useState("");
  const [notes, setNotes] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [design, setDesign] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [colour, setColour] = useState(false);
  const [yeti, setYeti] = useState(false);
  const [upgrade, setUpgrade] = useState(false);
  const [sideTwo, setSideTwo] = useState("");
  const [artwork, setArtwork] = useState("new");
  const [artworkRef, setArtworkRef] = useState("");
  const [pricing, setPricing] = useState(null);
  const [variant, setVariant] = useState(null);
  const [coat, setCoat] = useState("");
  const [font, setFont] = useState(ENGRAVING_FONTS[0].id);
  const isLaser = product?.method === "Laser Engraved";
  const fileRef = useRef(null);
  const { addItem, items: cartItems, hasNewArtwork } = useCart();

  useEffect(() => {
    api.get("/pricing-rules").then((r) => setPricing(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      setVariant(r.data.variants?.[0] || null);
      setCoat(r.data.coat_colours?.[0] || "");
    }).catch((e) => { if (e.response?.status === 404) setMissing(true); });
  }, [slug]);

  if (missing) {
    return (
      <main className="pt-40 pb-32 min-h-screen max-w-3xl mx-auto px-6 text-center" data-testid="product-not-found">
        <Seo title="Product not available | Ember & Steel Studio" description="This piece isn't in the shop right now. Browse the shop or request a quote." path={`/shop/${slug}`} noindex />
        <p className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#C87455]">Not available</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl text-white mt-6">This piece isn't in the shop right now.</h1>
        <p className="text-zinc-400 mt-4">It may be quote-only or temporarily unavailable. Browse the shop or ask us for a quote.</p>
        <div className="flex justify-center gap-3 mt-10">
          <Link to="/shop" data-testid="not-found-shop-link" className="bg-[#C87455] text-black hover:bg-[#A65B3E] transition-colors px-6 py-3 font-mono-tech text-[10px] uppercase tracking-[0.2em]">Back to shop</Link>
          <Link to="/business" data-testid="not-found-business-link" className="border border-zinc-700 text-zinc-200 hover:border-[#C87455] transition-colors px-6 py-3 font-mono-tech text-[10px] uppercase tracking-[0.2em]">Corporate gifts</Link>
        </div>
      </main>
    );
  }
  if (!product) {
    return <main className="pt-40 pb-32 min-h-screen" />;
  }

  const add = () => {
    addItem(product, {
      quantity,
      engraving: upgrade && product.upgrade_prompt && sideTwo.trim() ? `${engraving.trim()} | ${product.upgrade_prompt}: ${sideTwo.trim()}` : engraving.trim(),
      notes: notes.trim(),
      design_file_id: design?.file_id || "",
      design_filename: design?.filename || "",
      colour,
      colour_amount: colour ? product.colour_upgrade : 0,
      lookup_key: variant?.lookup_key || "",
      base_amount: variant?.amount || product.amount,
      size: variant?.label || "",
      pack_size: packSize,
      yeti,
      yeti_amount: yeti && variant ? variant.yeti_amount : 0,
      yeti_label: yeti && variant ? variant.yeti_label || "Yeti upgrade" : "",
      upgrade,
      upgrade_amount: upgrade ? product.upgrade_amount || 0 : 0,
      upgrade_label: upgrade ? product.upgrade_label || "" : "",
      artwork,
      artwork_reference: artwork === "reorder" ? artworkRef.trim().toUpperCase() : "",
      coat,
      font: isLaser && engraving.trim() ? font : "",
      image: displayImage,
    });
    toast.success(`${product.name} added to your order`);
  };

  const setupFeeAmount = pricing?.setup_fee?.amount || 1500;
  const baseAmount = variant ? variant.amount : product.amount;
  const displayImage = colour
    ? product.image_colour || product.image
    : yeti
    ? variant?.yeti_image || product.image_yeti || product.image
    : variant?.image || product.image;
  const unitPrice =
    baseAmount +
    (colour ? product.colour_upgrade || 0 : 0) +
    (yeti && variant ? variant.yeti_amount || 0 : 0) +
    (upgrade ? product.upgrade_amount || 0 : 0);
  const isPack = !!product.pack;
  const packSize = variant?.pack_size || 0;
  const unitsInCart = cartItems.filter((i) => i.slug === product.slug).reduce((n, i) => n + (i.pack_size || 1) * i.quantity, 0);
  const unitsAfterAdd = unitsInCart + (packSize || 1) * quantity;
  const overCap = !!product.max_units && unitsAfterAdd > product.max_units;
  const artFee = artwork === "new" && !hasNewArtwork ? setupFeeAmount : 0;
  const refMissing = artwork === "reorder" && artworkRef.trim().length < 4;

  const uploadDesign = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large — max 10MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDesign(data);
      toast.success("Design file attached");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed — try PNG, JPG, WEBP, SVG or PDF");
    }
    setUploading(false);
    e.target.value = "";
  };

  return (
    <main data-testid="product-detail-page" className="pt-32 md:pt-40 pb-32 min-h-screen">
      <Seo
        title={`${product.name} — ${product.method} ${product.material} | Ember & Steel Studio, Leduc AB`}
        description={productMetaDescription(product)}
        path={`/shop/${product.slug}`}
        image={product.image}
        type="product"
        jsonLd={[productSchema(product), breadcrumbSchema([["Home", "/"], ["Shop", "/shop"], [product.name, `/shop/${product.slug}`]])]}
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Link to="/shop" data-testid="back-to-shop-link" className="inline-flex items-center gap-2 font-mono-tech text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-[#C87455] transition-colors mb-10">
          <ArrowLeft size={14} /> Back to collection
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7 relative"
          >
            <div className="absolute inset-0 spotlight" />
            <div className="border border-zinc-800/60 overflow-hidden laser-card">
              {isLaser ? (
                <EngravingPreview product={product} image={displayImage} text={engraving} fontId={font} />
              ) : (
                <img key={displayImage} src={displayImage} alt={product.name} className="w-full aspect-square object-cover transition-opacity duration-500" data-testid="product-image" />
              )}
            </div>
            <div className="flex gap-6 mt-6 font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-600">
              <span>{product.method}</span>
              <span>·</span>
              <span>{product.material}</span>
              <span>·</span>
              <span>Made in-house</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5"
          >
            <span className="font-mono-tech text-xs uppercase tracking-[0.35em] text-[#C87455]">{product.subtitle}</span>
            <h1 className="font-serif-display font-light text-4xl md:text-5xl text-white mt-3 tracking-tight">
              {product.name}
            </h1>
            <p data-testid="product-price" className="font-mono-tech text-2xl text-[#A9B0B7] mt-4">
              {money(unitPrice)} <span className="text-xs text-zinc-500">CAD</span>
              <span className="ml-3 font-mono-tech text-xs text-[#C87455]" data-testid="price-artwork-note">{artwork === "reorder" ? "reorder · no setup fee" : hasNewArtwork ? "setup fee already in your order" : `+ ${money(setupFeeAmount)} artwork & setup · once per order`}</span>
              <span className="block font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-400 mt-2" data-testid="price-tax-notice">CAD · GST extra · Shipping calculated at checkout</span>
            </p>
            <p className="text-zinc-400 leading-relaxed mt-8">{product.description}</p>
            {isPack && (
              <div className="mt-6 border border-zinc-800 bg-[#0A0A0A] p-4 space-y-2" data-testid="pack-pricing-note">
                <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-[#C87455]">Pack pricing</p>
                <p className="text-zinc-400 text-sm leading-relaxed">Quantity below means the number of <strong className="text-zinc-200">packs</strong>, not individual cards. Pack prices already include volume pricing and are not combined with volume or promotional discount codes. A one-time $15 artwork &amp; setup fee applies per order for new designs; reorders of artwork we have on file carry no fee.</p>
                {product.max_units && <p className="text-zinc-500 text-xs">{product.max_units_message}</p>}
              </div>
            )}
            {(product.related_slug || product.quote_links?.length > 0) && (
              <div className="mt-4 flex flex-col gap-2" data-testid="product-links">
                {product.related_slug && <Link to={`/shop/${product.related_slug}`} data-testid="related-product-link" className="link-line inline-block self-start font-mono-tech text-[11px] uppercase tracking-[0.2em] text-[#C87455]">{product.related_label || "See related product →"}</Link>}
                {(product.quote_links || []).map((q) => <Link key={q.to} to={q.to} data-testid="product-quote-link" className="link-line inline-block self-start font-mono-tech text-[11px] uppercase tracking-[0.2em] text-zinc-300 hover:text-white">{q.label} →</Link>)}
              </div>
            )}
            {product.specs?.length > 0 && (
              <ul className="mt-6 space-y-2" data-testid="product-specs">
                {product.specs.map((sp, i) => (
                  <li key={i} className="flex gap-3 text-sm text-zinc-500 leading-relaxed">
                    <span className="text-[#C87455]/70 font-mono-tech text-[10px] mt-1.5">◆</span>
                    <span>{sp}</span>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-10 border-t border-zinc-900 pt-8 space-y-6">
              <div>
                <label className="flex items-center gap-2 font-mono-tech text-xs uppercase tracking-[0.2em] text-[#C87455] mb-3">
                  <Flame size={12} /> Personalise — {product.personalise}
                </label>
                <input
                  data-testid="engraving-input"
                  value={engraving}
                  onChange={(e) => setEngraving(e.target.value.slice(0, 60))}
                  placeholder="Your engraving text…"
                  className="w-full bg-transparent border-b border-zinc-800 focus:border-[#C87455] transition-colors rounded-none px-0 py-3 text-white placeholder-zinc-600 font-serif-display text-xl"
                />
                <p className="font-mono-tech text-[10px] text-zinc-600 mt-2">{engraving.length}/60 characters{isLaser && engraving.trim() ? " · previewing on the photo" : ""}</p>
              </div>
              {isLaser && (
                <div>
                  <label className="font-mono-tech text-xs uppercase tracking-[0.2em] text-zinc-500 block mb-3">Engraving style — <span className="text-zinc-300">{font}</span></label>
                  <FontPicker value={font} onChange={setFont} />
                  <p className="font-mono-tech text-[10px] text-zinc-600 mt-2">Type above to see your text on the piece. Final layout is confirmed in your digital proof.</p>
                </div>
              )}
              {product.variants && (
                <div>
                  <label className="font-mono-tech text-xs uppercase tracking-[0.2em] text-zinc-500 block mb-3">Size</label>
                  <div className="flex gap-3">
                    {product.variants.map((v) => (
                      <button
                        key={v.lookup_key}
                        type="button"
                        data-testid={`variant-${v.label}`}
                        onClick={() => { setVariant(v); setYeti(false); }}
                        className={`flex-1 py-4 border transition-colors text-center ${
                          variant?.lookup_key === v.lookup_key
                            ? "border-[#C87455] text-[#C87455] bg-[#C87455]/5"
                            : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-200"
                        }`}
                      >
                        <span className="font-serif-display text-xl block">{v.label}</span>
                        <span className="font-mono-tech text-xs text-zinc-500">{money(v.amount)}</span>
                      </button>
                    ))}
                  </div>
                  {variant?.includes && (
                    <p className="font-mono-tech text-[10px] uppercase tracking-[0.15em] text-zinc-400 mt-3" data-testid="variant-includes">{variant.includes}</p>
                  )}
                </div>
              )}
              {product.coat_colours?.length > 0 && (
                <div>
                  <label className="font-mono-tech text-xs uppercase tracking-[0.2em] text-zinc-500 block mb-3">
                    {product.colour_label || "Powder-coat colour"} — <span className="text-zinc-300">{coat}</span>
                  </label>
                  <div className="flex gap-3 flex-wrap">
                    {product.coat_colours.map((c) => {
                      const hex = { "Matte Black": "#1b1b1b", "White": "#EDEDEA", "Navy": "#1F3A5F", "Forest Green": "#2F4A3A", "Copper": "#C87455", "Black": "#111111", "Silver": "#C9CDD2", "Gold": "#C9A24A", "Rose Gold": "#D5A58E", "Blue": "#1F4FA3", "Red": "#A3261F", "Green": "#2E6B3A", "Purple": "#5B2E8A" }[c] || "#555";
                      return (
                        <button
                          key={c}
                          type="button"
                          title={c}
                          data-testid={`coat-${c.toLowerCase().replace(/ /g, "-")}`}
                          onClick={() => setCoat(c)}
                          className={`h-9 w-9 rounded-full border-2 transition-transform ${
                            coat === c ? "border-[#C87455] scale-110" : "border-zinc-700 hover:scale-105"
                          }`}
                          style={{ background: hex }}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
              {variant?.yeti_lookup && (
                <button
                  type="button"
                  data-testid="yeti-upgrade-toggle"
                  onClick={() => setYeti(!yeti)}
                  className={`w-full text-left border transition-colors duration-300 p-4 flex items-start gap-4 ${
                    yeti ? "border-[#C87455] bg-[#C87455]/10" : "border-zinc-800 hover:border-zinc-600"
                  }`}
                >
                  <span
                    className={`mt-0.5 h-4 w-4 shrink-0 border flex items-center justify-center ${
                      yeti ? "border-[#C87455] bg-[#C87455]" : "border-zinc-600"
                    }`}
                  >
                    {yeti && <span className="text-black text-[10px] leading-none">✓</span>}
                  </span>
                  <span>
                    <span className={`font-mono-tech text-xs uppercase tracking-[0.2em] block ${yeti ? "text-[#C87455]" : "text-zinc-300"}`}>
                      {variant.yeti_label || "Upgrade to Yeti"} — +{money(variant.yeti_amount)}
                    </span>
                    <span className="text-zinc-500 text-xs block mt-1 leading-relaxed">
                      {variant.yeti_description || "Genuine Yeti Rambler in place of our standard tumbler — same permanent laser engraving, legendary insulation."}
                    </span>
                  </span>
                </button>
              )}
              {product.upgrade_amount > 0 && product.upgrade_label && (
                <button
                  type="button"
                  data-testid="product-upgrade-toggle"
                  onClick={() => setUpgrade(!upgrade)}
                  className={`w-full text-left border transition-colors duration-300 p-4 flex items-start gap-4 ${
                    upgrade ? "border-[#C87455] bg-[#C87455]/10" : "border-zinc-800 hover:border-zinc-600"
                  }`}
                >
                  <span
                    className={`mt-0.5 h-4 w-4 shrink-0 border flex items-center justify-center ${
                      upgrade ? "border-[#C87455] bg-[#C87455]" : "border-zinc-600"
                    }`}
                  >
                    {upgrade && <span className="text-black text-[10px] leading-none">✓</span>}
                  </span>
                  <span>
                    <span className={`font-mono-tech text-xs uppercase tracking-[0.2em] block ${upgrade ? "text-[#C87455]" : "text-zinc-300"}`}>
                      {product.upgrade_label} — +{money(product.upgrade_amount)}
                    </span>
                    {product.upgrade_description && (
                      <span className="text-zinc-500 text-xs block mt-1 leading-relaxed">{product.upgrade_description}</span>
                    )}
                  </span>
                </button>
              )}
              {upgrade && product.upgrade_prompt && (
                <div data-testid="upgrade-prompt-field">
                  <label className="font-mono-tech text-xs uppercase tracking-[0.2em] text-[#C87455] block mb-3">{product.upgrade_prompt}</label>
                  <input
                    data-testid="upgrade-prompt-input"
                    value={sideTwo}
                    onChange={(e) => setSideTwo(e.target.value.slice(0, 60))}
                    placeholder="Text for the second side…"
                    className="w-full bg-transparent border-b border-zinc-800 focus:border-[#C87455] transition-colors rounded-none px-0 py-3 text-white placeholder-zinc-600 font-serif-display text-xl"
                  />
                  <p className="font-mono-tech text-[10px] text-zinc-600 mt-2">{sideTwo.length}/60 characters</p>
                </div>
              )}
              {product.colour_upgrade > 0 && (
                <button
                  type="button"
                  data-testid="colour-upgrade-toggle"
                  onClick={() => setColour(!colour)}
                  className={`w-full text-left border transition-colors duration-300 p-4 flex items-start gap-4 ${
                    colour ? "border-[#C87455] bg-[#C87455]/10" : "border-zinc-800 hover:border-zinc-600"
                  }`}
                >
                  <span
                    className={`mt-0.5 h-4 w-4 shrink-0 border flex items-center justify-center ${
                      colour ? "border-[#C87455] bg-[#C87455]" : "border-zinc-600"
                    }`}
                  >
                    {colour && <span className="text-black text-[10px] leading-none">✓</span>}
                  </span>
                  <span>
                    <span className={`font-mono-tech text-xs uppercase tracking-[0.2em] block ${colour ? "text-[#C87455]" : "text-zinc-300"}`}>
                      Oxidised colour engraving — +{money(product.colour_upgrade)}
                    </span>
                    <span className="text-zinc-500 text-xs block mt-1 leading-relaxed">
                      Iridescent blues, golds and purples marked directly into the bare steel by the MOPA fiber. No ink, no coating, never fades.
                    </span>
                  </span>
                </button>
              )}
              <div>
                <label className="font-mono-tech text-xs uppercase tracking-[0.2em] text-zinc-500 block mb-3">
                  Upload your logo or photo (optional)
                </label>
                <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp,.svg,.pdf" onChange={uploadDesign} className="hidden" data-testid="design-file-input" />
                {design ? (
                  <div className="flex items-center justify-between border border-[#C87455]/40 bg-[#C87455]/5 px-4 py-3" data-testid="design-file-attached">
                    <div className="flex items-center gap-3 min-w-0">
                      <Upload size={14} className="text-[#C87455] shrink-0" />
                      <span className="font-mono-tech text-xs text-zinc-300 truncate">{design.filename}</span>
                    </div>
                    <button data-testid="design-file-remove-btn" onClick={() => setDesign(null)} className="text-zinc-500 hover:text-red-400 transition-colors shrink-0 ml-3">
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <button
                    data-testid="design-upload-btn"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="w-full border border-dashed border-zinc-700 hover:border-[#C87455]/60 transition-colors px-4 py-4 font-mono-tech text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-zinc-200 flex items-center justify-center gap-3 disabled:opacity-60"
                  >
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    {uploading ? "Uploading…" : "Attach design file — PNG, JPG, SVG, PDF"}
                  </button>
                )}
                <p className="font-mono-tech text-[10px] text-zinc-600 mt-2">Your file arrives print-ready at the workbench with the order</p>
              </div>
              {pricing && (
                <ArtworkSetup
                  options={pricing.artwork_options}
                  fee={pricing.setup_fee}
                  mode={artwork}
                  onMode={setArtwork}
                  reference={artworkRef}
                  onReference={setArtworkRef}
                  feeInCart={hasNewArtwork}
                />
              )}
              <div>
                <label className="font-mono-tech text-xs uppercase tracking-[0.2em] text-zinc-500 block mb-3">
                  Notes for the maker (optional)
                </label>
                <textarea
                  data-testid="notes-input"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value.slice(0, 300))}
                  rows={2}
                  placeholder="Font preference, layout ideas, anything we should know…"
                  className="w-full bg-transparent border-b border-zinc-800 focus:border-[#C87455] transition-colors rounded-none px-0 py-3 text-white placeholder-zinc-600 text-sm resize-none"
                />
              </div>
            </div>

            {overCap && (
              <div className="mt-8 border border-amber-400/30 bg-amber-400/5 p-4" data-testid="unit-cap-notice">
                <p className="text-amber-200 text-sm leading-relaxed">{product.max_units_message || "This quantity is quoted individually."} You'd have {unitsAfterAdd} cards in your order.</p>
                <Link to={(product.quote_links || [])[1]?.to || (product.quote_links || [])[0]?.to || "/quote?type=business"} data-testid="unit-cap-quote-link" className="inline-block mt-3 font-mono-tech text-[11px] uppercase tracking-[0.2em] text-[#C87455] link-line">Request a custom quote →</Link>
              </div>
            )}
            <div className="flex items-center gap-6 mt-10">
              <div className="flex flex-col">
              {isPack && <span className="font-mono-tech text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1" data-testid="quantity-label">{product.quantity_label || "Quantity"}</span>}
              <div className="flex items-center border border-zinc-800">
                <button data-testid="qty-minus-btn" onClick={() => setQuantity(Math.max(1, quantity - 1))} className="px-4 py-3 text-zinc-400 hover:text-white transition-colors">
                  <Minus size={14} />
                </button>
                <span data-testid="qty-value" className="px-4 font-mono-tech text-sm text-white">{quantity}</span>
                <button data-testid="qty-plus-btn" onClick={() => setQuantity(quantity + 1)} className="px-4 py-3 text-zinc-400 hover:text-white transition-colors">
                  <Plus size={14} />
                </button>
              </div>
              </div>
              <button
                data-testid="add-to-cart-btn"
                onClick={add}
                disabled={overCap || refMissing}
                className="flex-1 bg-[#C87455] text-black hover:bg-[#A65B3E] transition-colors px-8 py-4 font-mono-tech uppercase tracking-[0.2em] text-sm disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {overCap ? "Quote required for this quantity" : refMissing ? "Enter your artwork reference" : <>Add to Order — {money(unitPrice * quantity + artFee)}</>}
              </button>
            </div>

            <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-600 mt-8 leading-relaxed">
              Proof sent before we burn anything · 5–7 business day turnaround after proof approval · Ships across Canada and to the USA by Canada Post
            </p>
          </motion.div>
        </div>

        <ReviewsSection slug={slug} />
      </div>
    </main>
  );
}
